import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import {
  Tooltip,
  IconButton,
  Chip,
  TablePagination,
  Box,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import RepeatIcon from "@material-ui/icons/Repeat";
import StopIcon from "@material-ui/icons/Stop";
import ScheduleIcon from "@material-ui/icons/Schedule";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];
    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }
    return [...state, ...newCampaigns];
  }
  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);
    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }
  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  filterContainer: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
  },
  filterTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  filterInput: {
    padding: "10px 12px",
    border: "1px solid #e8ecf1",
    borderRadius: "6px",
    fontSize: "13px",
    fontFamily: "inherit",
    background: "#fafbfc",
    transition: "all 0.2s ease",
    width: "100%",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      background: "white",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
  },
  filterLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    marginBottom: "6px",
    display: "block",
  },
  filterGroup: {
    display: "flex",
    flexDirection: "column",
  },
  filterActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "16px",
    paddingTop: "16px",
    borderTop: "1px solid #e8ecf1",
    flexWrap: "wrap",
  },
  filterButton: {
    padding: "10px 20px",
    background: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: "#2563eb",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
    },
    "&:disabled": {
      background: "#cbd5e1",
      cursor: "not-allowed",
      transform: "none",
    },
  },
  filterButtonSecondary: {
    padding: "10px 20px",
    background: "#e8ecf1",
    color: "#1a1a1a",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#d1d5db",
    },
    "&:disabled": {
      background: "#f0f0f0",
      cursor: "not-allowed",
      color: "#999",
    },
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "12px",
    marginBottom: "24px",
  },
  statCard: {
    background: "white",
    borderRadius: "8px",
    padding: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    textAlign: "center",
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    fontWeight: 500,
    marginBottom: "8px",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#3b82f6",
  },
  tableCard: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
    maxHeight: "calc(100vh - 400px)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f9fafb",
    borderBottom: "2px solid #e8ecf1",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  tableHeaderCell: {
    padding: "12px",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    whiteSpace: "nowrap",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  tableCell: {
    padding: "12px",
    fontSize: "13px",
    color: "#1a1a1a",
    textAlign: "center",
  },
  actionButtons: {
    display: "flex",
    gap: "4px",
    justifyContent: "center",
  },
  actionButton: {
    padding: "6px",
    minWidth: "32px",
    height: "32px",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    padding: "16px",
    background: "white",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    flexWrap: "wrap",
    gap: "12px",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#999",
  },
}));

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [stopRecurrenceModalOpen, setStopRecurrenceModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recurrenceFilter, setRecurrenceFilter] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error(i18n.t("campaigns.noPermission"));
        setTimeout(() => {
          history.push(`/`)
        }, 500); // Reduzir delay de 1000ms para 500ms
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 200); // Reduzir delay de 500ms para 200ms
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    const companyId = user.companyId;
    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    }
    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { 
          searchParam, 
          pageNumber, 
          pageSize,
          status: statusFilter,
          isRecurring: recurrenceFilter 
        },
      });
      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleRecurrenceFilterChange = (event) => {
    setRecurrenceFilter(event.target.value);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleStopRecurrence = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/stop-recurrence`);
      toast.success(i18n.t("campaigns.stopRecurrenceSuccess"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
    setStopRecurrenceModalOpen(false);
    setSelectedCampaign(null);
  };

  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage + 1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaigns.status.inactive");
      case "PROGRAMADA":
        return i18n.t("campaigns.status.scheduled");
      case "EM_ANDAMENTO":
        return i18n.t("campaigns.status.inProgress");
      case "CANCELADA":
        return i18n.t("campaigns.status.cancelled");
      case "FINALIZADA":
        return i18n.t("campaigns.status.finished");
      default:
        return val;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "INATIVA":
        return "default";
      case "PROGRAMADA":
        return "primary";
      case "EM_ANDAMENTO":
        return "secondary";
      case "CANCELADA":
        return "default";
      case "FINALIZADA":
        return "default";
      default:
        return "default";
    }
  };

  const formatRecurrenceType = (type) => {
    switch (type) {
      case "minutely":
        return i18n.t("campaigns.recurrenceTypes.minutely");
      case "hourly":
        return i18n.t("campaigns.recurrenceTypes.hourly");
      case "daily":
        return i18n.t("campaigns.recurrenceTypes.daily");
      case "weekly":
        return i18n.t("campaigns.recurrenceTypes.weekly");
      case "biweekly":
        return i18n.t("campaigns.recurrenceTypes.biweekly");
      case "monthly":
        return i18n.t("campaigns.recurrenceTypes.monthly");
      case "yearly":
        return i18n.t("campaigns.recurrenceTypes.yearly");
      default:
        return type;
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (user.profile === "user" && user?.showCampaign === "disabled") {
    return <ForbiddenPage />;
  }

  return (
    <div className={classes.container}>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${
            deletingCampaign.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title={i18n.t("campaigns.stopRecurrenceTitle")}
        open={stopRecurrenceModalOpen}
        onClose={() => setStopRecurrenceModalOpen(false)}
        onConfirm={() => handleStopRecurrence(selectedCampaign?.id)}
      >
        {i18n.t("campaigns.stopRecurrenceMessage")}
      </ConfirmationModal>

      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}

      {/* Header */}
      <div className={classes.header}>
        <h1 className={classes.title}>📢 {i18n.t("campaigns.title")}</h1>
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            className={classes.filterButton}
            onClick={() => history.push("/templates")}
          >
            {i18n.t("campaigns.templatesButton")}
          </button>
          <button
            className={classes.filterButton}
            onClick={handleOpenCampaignModal}
          >
            {i18n.t("campaigns.newCampaign")}
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className={classes.filterContainer}>
        <div className={classes.filterTitle}>{i18n.t("campaigns.searchFilters")}</div>
        <div className={classes.filterRow}>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>{i18n.t("campaigns.searchCampaign")}</label>
            <div style={{ position: "relative" }}>
              <SearchIcon style={{ position: "absolute", left: "10px", top: "10px", color: "#999", fontSize: "18px" }} />
              <input
                type="text"
                placeholder={i18n.t("campaigns.searchCampaignPlaceholder")}
                value={searchParam}
                onChange={handleSearch}
                className={classes.filterInput}
                style={{ paddingLeft: "36px" }}
              />
            </div>
          </div>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>{i18n.t("campaigns.filterByStatus")}</label>
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className={classes.filterInput}
            >
              <option value="">{i18n.t("campaigns.all")}</option>
              <option value="INATIVA">{i18n.t("campaigns.status.inactive")}</option>
              <option value="PROGRAMADA">{i18n.t("campaigns.status.scheduled")}</option>
              <option value="EM_ANDAMENTO">{i18n.t("campaigns.status.inProgress")}</option>
              <option value="CANCELADA">{i18n.t("campaigns.status.cancelled")}</option>
              <option value="FINALIZADA">{i18n.t("campaigns.status.finished")}</option>
            </select>
          </div>
          <div className={classes.filterGroup}>
            <label className={classes.filterLabel}>{i18n.t("campaigns.filterByRecurrence")}</label>
            <select
              value={recurrenceFilter}
              onChange={handleRecurrenceFilterChange}
              className={classes.filterInput}
            >
              <option value="">{i18n.t("campaigns.allFemale")}</option>
              <option value="true">{i18n.t("campaigns.recurring")}</option>
              <option value="false">{i18n.t("campaigns.single")}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Estatísticas */}
      <div className={classes.statsGrid}>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>{i18n.t("campaigns.totalCampaigns")}</div>
          <div className={classes.statValue}>{totalCount}</div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>{i18n.t("campaigns.active")}</div>
          <div className={classes.statValue} style={{ color: "#10b981" }}>
            {campaigns.filter((c) => c.status === "EM_ANDAMENTO").length}
          </div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>{i18n.t("campaigns.scheduled")}</div>
          <div className={classes.statValue} style={{ color: "#3b82f6" }}>
            {campaigns.filter((c) => c.status === "PROGRAMADA").length}
          </div>
        </div>
        <div className={classes.statCard}>
          <div className={classes.statLabel}>{i18n.t("campaigns.recurrent")}</div>
          <div className={classes.statValue} style={{ color: "#8b5cf6" }}>
            {campaigns.filter((c) => c.isRecurring).length}
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className={classes.tableCard}>
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead className={classes.tableHeader}>
              <tr>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.name")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.status")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.recurrence")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.listTag")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.whatsapp")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.scheduling")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.nextExecution")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.conclusion")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.confirmation")}</th>
                <th className={classes.tableHeaderCell}>{i18n.t("campaigns.tableHeaders.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id} className={classes.tableRow}>
                  <td className={classes.tableCell}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      {campaign.isRecurring && (
                        <Tooltip title={i18n.t("campaigns.tableData.recurringCampaign")}>
                          <RepeatIcon color="primary" fontSize="small" />
                        </Tooltip>
                      )}
                      {campaign.name}
                    </div>
                  </td>
                  <td className={classes.tableCell}>
                    <Chip
                      label={formatStatus(campaign.status)}
                      color={getStatusColor(campaign.status)}
                      size="small"
                    />
                  </td>
                  <td className={classes.tableCell}>
                    {campaign.isRecurring ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <Chip
                          label={formatRecurrenceType(campaign.recurrenceType)}
                          size="small"
                          style={{ backgroundColor: "#8b5cf6", color: "white" }}
                        />
                        <span style={{ fontSize: "0.75rem", color: "#666" }}>
                          {campaign.executionCount || 0} {i18n.t("campaigns.recurrenceTypes.executions")}
                        </span>
                      </div>
                    ) : (
                      <Chip label={i18n.t("campaigns.recurrenceTypes.single")} variant="outlined" size="small" />
                    )}
                  </td>
                  <td className={classes.tableCell}>
                    {campaign.contactListId
                      ? campaign.contactList?.name || i18n.t("campaigns.tableData.listRemoved")
                      : campaign.tagListId && campaign.tagListId !== "Nenhuma"
                      ? `${i18n.t("campaigns.tableData.tagPrefix")} ${campaign.tagListId}`
                      : i18n.t("campaigns.tableData.notDefined")}
                  </td>
                  <td className={classes.tableCell}>
                    {campaign.whatsappId
                      ? campaign.whatsapp?.name || i18n.t("campaigns.tableData.whatsappRemoved")
                      : i18n.t("campaigns.tableData.notDefinedMasc")}
                  </td>
                  <td className={classes.tableCell}>
                    {campaign.scheduledAt
                      ? datetimeToClient(campaign.scheduledAt)
                      : i18n.t("campaigns.tableData.noSchedule")}
                  </td>
                  <td className={classes.tableCell}>
                    {campaign.isRecurring && campaign.nextScheduledAt ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                        <ScheduleIcon fontSize="small" color="primary" />
                        {datetimeToClient(campaign.nextScheduledAt)}
                      </div>
                    ) : campaign.isRecurring && campaign.status === "FINALIZADA" ? (
                      <span style={{ color: "#666", fontSize: "0.875rem" }}>
                        {i18n.t("campaigns.tableData.finished")}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className={classes.tableCell}>
                    {campaign.completedAt
                      ? datetimeToClient(campaign.completedAt)
                      : i18n.t("campaigns.tableData.notCompleted")}
                  </td>
                  <td className={classes.tableCell}>
                    {campaign.confirmation ? i18n.t("campaigns.tableData.yes") : i18n.t("campaigns.tableData.no")}
                  </td>
                  <td className={classes.tableCell}>
                    <div className={classes.actionButtons}>
                      {campaign.status === "EM_ANDAMENTO" && (
                        <Tooltip title={i18n.t("campaigns.tooltips.stopCampaign")}>
                          <IconButton
                            onClick={() => cancelCampaign(campaign)}
                            size="small"
                            className={classes.actionButton}
                          >
                            <PauseCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {campaign.status === "CANCELADA" && (
                        <Tooltip title={i18n.t("campaigns.tooltips.restartCampaign")}>
                          <IconButton
                            onClick={() => restartCampaign(campaign)}
                            size="small"
                            className={classes.actionButton}
                          >
                            <PlayCircleOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {campaign.isRecurring && campaign.status !== "FINALIZADA" && (
                        <Tooltip title={i18n.t("campaigns.tooltips.stopRecurrence")}>
                          <IconButton
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setStopRecurrenceModalOpen(true);
                            }}
                            size="small"
                            color="secondary"
                            className={classes.actionButton}
                          >
                            <StopIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={i18n.t("campaigns.tooltips.report")}>
                        <IconButton
                          onClick={() =>
                            history.push(`/campaign/${campaign.id}/report`)
                          }
                          size="small"
                          className={classes.actionButton}
                        >
                          <DescriptionIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={i18n.t("campaigns.tooltips.edit")}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditCampaign(campaign)}
                          className={classes.actionButton}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={i18n.t("campaigns.tooltips.delete")}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setConfirmModalOpen(true);
                            setDeletingCampaign(campaign);
                          }}
                          className={classes.actionButton}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
              {loading && <TableRowSkeleton columns={10} />}
            </tbody>
          </table>
        </div>

        {campaigns.length === 0 && !loading && (
          <div className={classes.emptyState}>
            <p>{i18n.t("campaigns.noCampaignsFound")}</p>
          </div>
        )}

        {/* Paginação */}
        <div className={classes.paginationContainer}>
          <Box color="text.secondary" fontSize="0.875rem">
            Total: {totalCount} {i18n.t("campaigns.title").toLowerCase()}
          </Box>
          <TablePagination
            component="div"
            count={totalCount}
            page={pageNumber - 1}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            labelRowsPerPage={i18n.t("campaigns.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </div>
      </div>
    </div>
  );
};

export default Campaigns;
